// src/components/countries/CountrySyncModal.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Modal from '../common/Modal';
import LoadingSpinner from '../common/LoadingSpinner';
import apiClient from '../../services/ApiClient';
import styles from './CountrySyncModal.module.css';

// Backend BaseResponse döndürüyor: { data: {...} }. Bazı uçlar düz gövde döndürebildiği
// için ikisini de tolere ediyoruz.
const unwrap = (responseData) => {
    if (responseData && typeof responseData === 'object' && responseData.data) {
        return responseData.data;
    }
    return responseData;
};

// 404 gövdesi: "'virgin' KeepGo coverage'ında bulunamadı. Yakın yazımlar: British Virgin
// Islands, United States Virgin Islands" — öneri kısmını çıkarıp seçenek olarak sunuyoruz.
const parseSuggestions = (message) => {
    if (!message) return [];
    const marker = 'Yakın yazımlar:';
    const index = message.indexOf(marker);
    if (index === -1) return [];
    return message
        .slice(index + marker.length)
        .split(',')
        .map((s) => s.trim().replace(/\.$/, ''))
        .filter(Boolean);
};

const readError = (err, fallback) =>
    err.response?.data?.error || err.response?.data?.message || fallback;

// nextSteps metinlerini ilgili panel ekranına bağla.
const stepLink = (step) => {
    const text = (step || '').toLowerCase();
    if (text.includes('fiyat') || text.includes('base_price_try') || text.includes('google play')) {
        return { to: '/packages', label: 'Paket fiyatları ekranına git' };
    }
    if (text.includes('çeviri') || text.includes('countries-data') || text.includes('aktif')) {
        return { to: '/countries', label: 'Ülkeler ekranına git' };
    }
    return null;
};

const BundleChips = ({ items }) => (
    <ul className={styles.bundleList}>
        {items.map((item) => (
            <li key={`${item.originalDataId}-${item.bundleName}`} className={styles.bundleRow}>
                <span className={styles.bundleName}>{item.bundleName}</span>
                {item.bundleType && <span className={styles.bundleType}>{item.bundleType}</span>}
                <span className={`${styles.bundleId} num`}>#{item.originalDataId}</span>
                {item.reason && <span className={styles.bundleReason}>{item.reason}</span>}
            </li>
        ))}
    </ul>
);

const CountrySyncModal = ({ isOpen, onClose, countryName, onApplied }) => {
    // Modal içinde ülke adı değişebilir (404 önerisi seçilirse).
    const [targetCountry, setTargetCountry] = useState(countryName);
    const [plan, setPlan] = useState(null);          // dry run yanıtı
    const [applied, setApplied] = useState(null);    // dryRun:false yanıtı
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [isApplying, setIsApplying] = useState(false);
    const [error, setError] = useState(null);
    const [suggestions, setSuggestions] = useState([]);

    // dryRun alanı GÖNDERİLMEZSE backend varsayılan olarak dry run yapar.
    const fetchPlan = useCallback(async (country) => {
        setIsPreviewing(true);
        setError(null);
        setSuggestions([]);
        setPlan(null);
        try {
            const response = await apiClient.post('/admin/bundles/sync-country', { country });
            setPlan(unwrap(response.data));
        } catch (err) {
            console.error(`Ülke sync önizlemesi alınamadı (${country}):`, err);
            const message = readError(err, 'Önizleme alınamadı. Lütfen tekrar deneyin.');
            setError(message);
            setSuggestions(parseSuggestions(message));
        } finally {
            setIsPreviewing(false);
        }
    }, []);

    useEffect(() => {
        if (!isOpen || !countryName) return;
        setTargetCountry(countryName);
        setApplied(null);
        fetchPlan(countryName);
    }, [isOpen, countryName, fetchPlan]);

    const handleApply = async () => {
        setIsApplying(true);
        setError(null);
        try {
            // KRİTİK: dryRun açıkça false gönderilmezse hiçbir satır yazılmaz.
            const response = await apiClient.post('/admin/bundles/sync-country', {
                country: targetCountry,
                dryRun: false,
            });
            const data = unwrap(response.data);
            if (data?.dryRun) {
                // Beklenmedik: backend yine dry run döndürdüyse yazma olmamıştır.
                setError('Sunucu isteği dry run olarak işledi; hiçbir satır yazılmadı. Tekrar deneyin.');
                setPlan(data);
                return;
            }
            setApplied(data);
            toast.success(`"${data?.country || targetCountry}" eklendi.`);
            if (onApplied) onApplied();
        } catch (err) {
            console.error(`Ülke sync uygulanamadı (${targetCountry}):`, err);
            setError(readError(err, 'Ekleme başarısız oldu. Hiçbir satır yazılmamıştır.'));
        } finally {
            setIsApplying(false);
        }
    };

    const renderPlanBody = (data, isApplyResult) => {
        const coverageInserted = data.coverageInserted || [];
        const coverageSkipped = data.coverageSkipped || [];
        const refillsInserted = data.refillsInserted || [];
        const refillsSkipped = data.refillsSkipped || [];

        return (
            <>
                <div className={styles.summaryRow}>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Ülke</span>
                        <span className={styles.summaryValue}>{data.country}</span>
                    </div>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Kapsayan paket</span>
                        <span className={`${styles.summaryValue} num`}>{data.matchedBundles ?? '-'}</span>
                    </div>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Coverage</span>
                        <span className={`${styles.summaryValue} num`}>+{coverageInserted.length}</span>
                    </div>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Paket (refill)</span>
                        <span className={`${styles.summaryValue} num`}>+{refillsInserted.length}</span>
                    </div>
                </div>

                <section className={styles.section}>
                    <h4 className={styles.sectionTitle}>
                        {isApplyResult ? 'Eklenen coverage satırları' : 'Eklenecek coverage satırları'}
                        <span className={`${styles.sectionCount} num`}>{coverageInserted.length}</span>
                    </h4>
                    {coverageInserted.length > 0
                        ? <BundleChips items={coverageInserted} />
                        : <p className={styles.emptyNote}>Yeni coverage satırı yok.</p>}
                </section>

                <section className={styles.section}>
                    <h4 className={styles.sectionTitle}>
                        {isApplyResult ? 'Eklenen paketler' : 'Eklenecek paketler'}
                        <span className={`${styles.sectionCount} num`}>{refillsInserted.length}</span>
                    </h4>
                    {refillsInserted.length > 0 ? (
                        <div className={styles.tableWrapper}>
                            <table className={styles.refillTable}>
                                <thead>
                                    <tr>
                                        <th>Paket</th>
                                        <th>Başlık</th>
                                        <th>MB</th>
                                        <th>Gün</th>
                                        <th>USD</th>
                                        {isApplyResult && <th>Refill ID</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {refillsInserted.map((refill, index) => (
                                        <tr key={refill.refillId || `${refill.originalDataId}-${index}`}>
                                            <td>{refill.bundleName}</td>
                                            <td>{refill.title}</td>
                                            <td className="num">{refill.amountMb}</td>
                                            <td className="num">{refill.amountDays}</td>
                                            <td className="num">{refill.priceUsd}</td>
                                            {isApplyResult && (
                                                <td className="num">{refill.refillId ?? '-'}</td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className={styles.emptyNote}>Yeni paket yok.</p>
                    )}
                </section>

                {(coverageSkipped.length > 0 || refillsSkipped.length > 0) && (
                    <section className={styles.section}>
                        <h4 className={styles.sectionTitle}>
                            Atlananlar
                            <span className={`${styles.sectionCount} num`}>
                                {coverageSkipped.length + refillsSkipped.length}
                            </span>
                        </h4>
                        <p className={styles.emptyNote}>
                            Zaten var olan satırlara dokunulmadı; mevcut fiyatlar korundu.
                        </p>
                        {coverageSkipped.length > 0 && <BundleChips items={coverageSkipped} />}
                        {refillsSkipped.length > 0 && <BundleChips items={refillsSkipped} />}
                    </section>
                )}
            </>
        );
    };

    const renderPreview = () => (
        <div className={styles.body}>
            {renderPlanBody(plan, false)}

            <div className={styles.warning}>
                Ülke <strong>yayına kapalı</strong> eklenecek (<code>is_active=false</code>). Çeviriler
                ve fiyatlar girilip aktifleştirilene kadar uygulamada görünmez, satışa çıkmaz.
            </div>

            {error && <p className={styles.errorText}>{error}</p>}

            <div className={styles.actions}>
                <button type="button" className="ghost" onClick={onClose} disabled={isApplying}>
                    Vazgeç
                </button>
                <button type="button" onClick={handleApply} disabled={isApplying}>
                    {isApplying ? 'Ekleniyor…' : 'Onayla ve ekle'}
                </button>
            </div>
        </div>
    );

    const renderApplied = () => {
        const steps = applied.nextSteps || [];
        return (
            <div className={styles.body}>
                <div className={styles.successBanner}>
                    <strong>{applied.country}</strong> eklendi. Satırlar yayına kapalı
                    (<code>is_active=false</code>) — uygulamada henüz görünmüyor.
                </div>

                {renderPlanBody(applied, true)}

                {steps.length > 0 && (
                    <section className={styles.section}>
                        <h4 className={styles.sectionTitle}>Sıradaki adımlar</h4>
                        <ol className={styles.stepList}>
                            {steps.map((step, index) => {
                                const link = stepLink(step);
                                return (
                                    <li key={index} className={styles.stepItem}>
                                        <span>{step}</span>
                                        {link && (
                                            <Link to={link.to} className={styles.stepLink} onClick={onClose}>
                                                {link.label} →
                                            </Link>
                                        )}
                                    </li>
                                );
                            })}
                        </ol>
                    </section>
                )}

                <div className={styles.actions}>
                    <button type="button" onClick={onClose}>Kapat</button>
                </div>
            </div>
        );
    };

    const renderError = () => (
        <div className={styles.body}>
            <div className={styles.errorPanel}>
                <span className={styles.errorIcon} aria-hidden="true">⚠</span>
                <p className={styles.errorText}>{error}</p>
            </div>

            {suggestions.length > 0 && (
                <section className={styles.section}>
                    <h4 className={styles.sectionTitle}>Yakın yazımlar</h4>
                    <div className={styles.suggestionRow}>
                        {suggestions.map((suggestion) => (
                            <button
                                key={suggestion}
                                type="button"
                                className={`${styles.suggestionButton} ghost`}
                                onClick={() => {
                                    setTargetCountry(suggestion);
                                    fetchPlan(suggestion);
                                }}
                            >
                                {suggestion}
                            </button>
                        ))}
                    </div>
                </section>
            )}

            <div className={styles.actions}>
                <button type="button" className="ghost" onClick={onClose}>Kapat</button>
                <button type="button" onClick={() => fetchPlan(targetCountry)}>Tekrar dene</button>
            </div>
        </div>
    );

    let title = `Ülke ekle: ${targetCountry || ''}`;
    if (applied) title = `Eklendi: ${applied.country}`;
    else if (plan) title = `Önizleme (dry run): ${plan.country}`;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title}>
            {isPreviewing ? (
                <div className={styles.loadingBox}>
                    <LoadingSpinner />
                    <p>KeepGo ile karşılaştırılıyor…</p>
                </div>
            ) : applied ? (
                renderApplied()
            ) : plan ? (
                renderPreview()
            ) : (
                renderError()
            )}
        </Modal>
    );
};

export default CountrySyncModal;
